'use client'

import { Monitor, Grid3x3, Layers, Crown } from "lucide-react"
import { useLanguage } from "@/lib/i18n/language-provider"
import { ScrollReveal } from "@/components/scroll-reveal"
import { useEffect, useRef } from "react"
import { captureEvent } from "@/lib/posthog-helper"

export function Features() {
  const { t } = useLanguage()
  const hasTrackedRef = useRef(false)

  // Track when features section is visible
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTrackedRef.current) {
            hasTrackedRef.current = true
            captureEvent('signup_flow:features_view', {
              scroll_depth: 'features_section',
            })
          }
        })
      },
      { threshold: 0.3 }
    )

    const section = document.querySelector('[data-features-section]')
    if (section) {
      observer.observe(section)
    }

    return () => observer.disconnect()
  }, [])

  const features = [
    {
      icon: Monitor,
      title: t('landing.features.testPattern.title'),
      description: t('landing.features.testPattern.desc'),
      isPro: false,
    },
    {
      icon: Grid3x3,
      title: t('landing.features.synoptic.title'),
      description: t('landing.features.synoptic.desc'),
      isPro: true,
    },
    {
      icon: Layers,
      title: t('landing.features.multigrid.title'),
      description: t('landing.features.multigrid.desc'),
      isPro: true,
    },
  ]

  return (
    <section className="py-16 md:py-32 relative overflow-hidden" data-features-section>
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="w-full relative z-10 px-4 md:px-6 flex flex-col items-center">
        <ScrollReveal animation="bounce-in" duration={1}>
          <div className="text-center mb-12 md:mb-20">
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-4 md:mb-6">{t('landing.features.title')}</h2>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed px-4">
              {t('landing.features.subtitle')}
            </p>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 w-full max-w-7xl">
          {features.map((feature, index) => {
            const animations = ['flip-in', 'rotate-in', 'scale-in-bounce'];
            return (
              <ScrollReveal
                key={index}
                animation={animations[index]}
                delay={index * 0.15}
                duration={0.8}
                className="group relative flex justify-center"
              >
                <div className="absolute -inset-1 bg-foreground/5 opacity-0 group-hover:opacity-100 transition" />
                <div className="relative border-2 border-foreground/10 p-6 md:p-8 bg-background hover:border-foreground/30 transition-all w-full">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4 md:mb-6 p-3 md:p-4 border-2 border-foreground/20 w-fit">
                      <feature.icon className="w-6 h-6 md:w-8 md:h-8" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                        <h3 className="text-xl md:text-2xl font-bold tracking-tight">{feature.title}</h3>
                        {feature.isPro && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-foreground/20 text-xs font-semibold">
                            <Crown className="w-3 h-3" />
                            Pro
                          </span>
                        )}
                      </div>
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">{feature.description}</p>
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            );
          })}
        </div>
      </div>
    </section>
  )
}

